import React, { useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import { IPrintFileJobsQueryParams, IPrintFileLineItem, PRINT_FILE_JOBS_LIMIT_OPTIONS } from '../../api/printFileJobs';
import { ILightboxImage } from '../../lib/printFileJobs/ui';
import PrintFileJobsRow from './PrintFileJobsRow';
import ImageLightbox from './ImageLightbox';

interface Props {
    items: IPrintFileLineItem[];
    paging: IPrintFileJobsQueryParams;
    pagingUpdate: (params: IPrintFileJobsQueryParams) => void;
    expandedIds: Record<string, boolean>;
    onToggle: (lineItemId: string) => void;
}

export default function PrintFileJobsTable({ items, paging, pagingUpdate, expandedIds, onToggle }: Props) {
    const [lightbox, setLightbox] = useState<{ images: ILightboxImage[]; index: number | null }>({ images: [], index: null });

    const handleChangePage = (_: unknown, newPage: number) => {
        pagingUpdate({ ...paging, page: newPage + 1 });
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        pagingUpdate({ ...paging, page: 1, limit: +e.target.value });
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: '72vh', position: 'relative' }}>
                <Table stickyHeader size="small" aria-label="print file jobs table">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell style={{ minWidth: 150 }}>Line item</TableCell>
                            <TableCell style={{ minWidth: 150 }}>Order / SKU</TableCell>
                            <TableCell style={{ minWidth: 170 }}>Print file</TableCell>
                            <TableCell style={{ minWidth: 170 }}>Current job</TableCell>
                            <TableCell align="center" style={{ minWidth: 80 }}>Jobs</TableCell>
                            <TableCell align="center" style={{ minWidth: 190 }}>Images</TableCell>
                            <TableCell style={{ minWidth: 160 }}>Updated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => (
                            <PrintFileJobsRow
                                key={item.lineItemId}
                                item={item}
                                expanded={!!expandedIds[item.lineItemId]}
                                onToggle={() => onToggle(item.lineItemId)}
                                onOpenImage={(images, index) => setLightbox({ images, index })}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={PRINT_FILE_JOBS_LIMIT_OPTIONS}
                component="div"
                count={paging.total || 0}
                rowsPerPage={paging.limit || 20}
                page={Math.max(0, (paging.page || 1) - 1)}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <ImageLightbox
                images={lightbox.images}
                index={lightbox.index}
                onClose={() => setLightbox((s) => ({ ...s, index: null }))}
                onIndexChange={(index) => setLightbox((s) => ({ ...s, index }))}
            />
        </Paper>
    );
}
